import { Component, inject, signal } from '@angular/core';
import { applyWhen, email, form, FormField, FormRoot, minLength, required, submit } from '@angular/forms/signals';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { passwordsMatch, passwordStrength, phoneNumber } from '@app/shared/forms/rules';
import { UsersDataService } from '@app/shared/services/users-data.service';
import { CompanyUsersPageParams } from '@app/shared/types/models/page-params';
import { CreateUserRequestBody } from '@app/shared/types/models/user/user-dto.model';
import { User } from '@app/shared/types/models/user/user.model';
import { UiDialogComponent } from '@app/shared/ui/dialog/ui-dialog.component';
import { UiInputComponent } from '@app/shared/ui/input/ui-input.component';
import { ProffeoUiInputValue } from '@app/shared/ui/types/input.types';
import { TranslatePipe } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

export interface CreateUserDialogData {
  pageParams: CompanyUsersPageParams;
}

interface CreateUserFormModel {
  email: string;
  password: string;
  passwordConfirm: string;
  firstName: string;
  lastName: string;
  notes: string;
  phoneNumbers: string;
  address: string;
}

@Component({
  selector: 'proffeo-create-user-dialog',
  imports: [
    MatDialogModule,
    TranslatePipe,
    UiDialogComponent,
    FormField,
    FormRoot,
    UiButtonComponent,
    UiInputComponent
  ],
  templateUrl: './create-user-dialog.component.html',
  styleUrls: ['./create-user-dialog.component.scss']
})
export class CreateUserDialogComponent {
  private readonly dialogData: CreateUserDialogData = inject(MAT_DIALOG_DATA);
  private readonly userService: UsersDataService = inject(UsersDataService);
  private readonly dialogRef: MatDialogRef<CreateUserDialogComponent, User> = inject(
    MatDialogRef<CreateUserDialogComponent, User>
  );

  protected readonly model = signal<CreateUserFormModel>({
    email: '',
    password: '',
    passwordConfirm: '',
    firstName: '',
    lastName: '',
    notes: '',
    phoneNumbers: '',
    address: ''
  });
  protected readonly form = form(this.model, path => {
    required(path.email, { message: 'ui-forms-this-field-is-required' });
    email(path.email, { message: 'invalid-email' });
    // The password is optional: its rules apply only once one is typed.
    applyWhen(
      path,
      ({ valueOf }) => !!valueOf(path.password).trim(),
      passwordPath => {
        minLength(passwordPath.password, 8, { message: 'password-validation-message' });
        passwordStrength(passwordPath.password, { message: 'password-validation-message' });
        required(passwordPath.passwordConfirm, { message: 'ui-forms-this-field-is-required' });
      }
    );
    passwordsMatch(path.passwordConfirm, path.password, { message: 'confirm-password-does-not-match-password' });
    required(path.firstName, { message: 'ui-forms-this-field-is-required' });
    required(path.lastName, { message: 'ui-forms-this-field-is-required' });
    phoneNumber(path.phoneNumbers, {
      message: 'user-profile-page.basic-data-view.invalid-phone-number-validation-msg'
    });
  });

  /** Clearing the password also clears its confirmation. */
  protected onPasswordChange(password: ProffeoUiInputValue): void {
    if (!String(password ?? '').trim()) {
      this.model.update(value => ({ ...value, passwordConfirm: '' }));
    }
  }

  protected saveUserData(): void {
    const companyId = this.dialogData.pageParams.companyId;

    void submit(this.form, {
      action: async () => {
        try {
          const user = await firstValueFrom(this.userService.createUser(companyId, this.toRequest()));
          this.dialogRef.close(user);
        } catch {
          this.dialogRef.close();
        }
      }
    });
  }

  /** Same payload as before the Signal Forms migration: every form field, `password` trimmed and only when set. */
  private toRequest(): CreateUserRequestBody {
    const { password, ...userFields } = this.model();
    return {
      ...userFields,
      ...(password.trim() ? { password: password.trim() } : {})
    };
  }
}
